import {
  constructAmountRequirementsError,
  isValidAmount,
  setSourceAccount,
  toXdrAmount,
  toXdrPrice,
} from "../util/operations.js";
import {
  Asset,
  CreatePassiveSellOfferOp,
  Int64,
  Operation,
  OperationBody,
  Price,
} from "../../xdr/index.js";
import { CreatePassiveSellOfferOpts, OperationAttributes } from "./types.js";

/**
 * A "create passive offer" operation creates an offer that won't consume a
 * counter offer that exactly matches this offer. This is useful for offers
 * just used as 1:1 exchanges for path payments. Use manage offer to manage
 * this offer after using this operation to create it.
 * @param opts - Options object
 *   - `selling`: What you're selling.
 *   - `buying`: What you're buying.
 *   - `amount`: The total amount you're selling. If 0, deletes the offer.
 *   - `price`: Price of 1 unit of `selling` in terms of `buying`.
 *     - `n`: If `opts.price` is an object: the price numerator
 *     - `d`: If `opts.price` is an object: the price denominator
 *   - `source`: The source account (defaults to transaction source).
 * @throws when the best rational approximation of `price` cannot be found.
 */
export function createPassiveSellOffer(
  opts: CreatePassiveSellOfferOpts,
): Operation {
  const selling: Asset = opts.selling.toXdrObject();
  const buying: Asset = opts.buying.toXdrObject();

  if (!isValidAmount(opts.amount)) {
    throw new TypeError(constructAmountRequirementsError("amount"));
  }

  const amount: Int64 = toXdrAmount(opts.amount);

  if (opts.price === undefined) {
    throw new TypeError("price argument is required");
  }

  const price: Price = toXdrPrice(opts.price);

  const createPassiveSellOfferOp = new CreatePassiveSellOfferOp({
    selling,
    buying,
    amount,
    price,
  });

  const opAttributes: OperationAttributes = {
    sourceAccount: null,
    body: OperationBody.createPassiveSellOffer(createPassiveSellOfferOp),
  };

  setSourceAccount(opAttributes, opts);

  return new Operation(opAttributes);
}
